import styled from "@emotion/styled";
import React, { useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { getEmailRestrictions } from "../../api/User";
import { SettingsHelpVideosAtom } from "../../atoms/SettingsHelpVideos";
import { UserAtom } from "../../atoms/UserAtom";
import { UserEmailRestrictionsAtom } from "../../atoms/UserEmailRestrictionsAtom";
import { HelpVideoNameMap, IHelpContexts } from "../../types/HelpVideosTypes";
import countries from "../../utils/countries.json";
import Loading from "../Loading";
import BlockingHeader from "./Blocking/BlockingHeader";
import BlockingTable from "./Blocking/BlockingTable";

const Container = styled.div`
  max-width: 1200px;
  min-width: 1000px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }

  .content {
    display: flex;
    align-items: flex-start;
    justify-content: start;

    grid-gap: 32px;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
  }
`;

export enum BlockingTypes {
  EMAIL,
  PHONE,
  KEYWORD,
  DOMAIN,
  COUNTRIES,
  COUNTRY_CODE,
  DEVICE_IP,
  DEVICE_ID,
}

export default function Blocking() {
  const user = useRecoilValue(UserAtom);
  const [selected, setSelected] = useState(BlockingTypes.EMAIL);
  const [, setUserEmailRestrictions] = useRecoilState(
    UserEmailRestrictionsAtom
  );
  const [, setHelpVideo] = useRecoilState(SettingsHelpVideosAtom);

  useEffect(() => {
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.SettingsBlocking],
      helpContext: IHelpContexts.SettingsBlocking,
    });
  }, []);

  useEffect(() => {
    (async () => {
      const emailRestrictions = await getEmailRestrictions();
      setUserEmailRestrictions(emailRestrictions);
    })();
  }, []);

  function handleSelected(vle: BlockingTypes) {
    setSelected(vle);
  }

  const returnBody = useMemo(() => {
    if (!user?.emailRestrictions) return <Loading />;

    switch (selected) {
      case BlockingTypes.EMAIL:
        return (
          <BlockingTable
            type={BlockingTypes.EMAIL}
            values={user.emailRestrictions.emailsBlocked}
            selected={selected}
          />
        );
      case BlockingTypes.PHONE:
        return (
          <BlockingTable
            type={BlockingTypes.PHONE}
            selected={selected}
            values={user.emailRestrictions.phoneNumbersBlocked}
          />
        );
      case BlockingTypes.KEYWORD:
        return (
          <BlockingTable
            type={BlockingTypes.KEYWORD}
            selected={selected}
            values={user.emailRestrictions.keywordsBlocked.concat(
              user.emailRestrictions.keywordsFlagged,
              user.emailRestrictions.keywordsSuspended,

              // Partials
              // I'm concatenating the keyword with the type to access
              // the type in the component and be able to delete and display it correctly,
              // this change was made because in the old way if I had the same keyword
              // it was giving too many errors.
              user.emailRestrictions.partialKeywordsBlocked
                ? user.emailRestrictions.partialKeywordsBlocked.map(
                    (k) => `${k.keyword}_partial_${k.type}`
                  )
                : [],
              user.emailRestrictions.partialKeywordsFlagged
                ? user.emailRestrictions.partialKeywordsFlagged.map(
                    (k) => `${k.keyword}_partial_${k.type}`
                  )
                : [],
              user.emailRestrictions.partialKeywordsSuspended
                ? user.emailRestrictions.partialKeywordsSuspended.map(
                    (k) => `${k.keyword}_partial_${k.type}`
                  )
                : []
            )}
            keyword={{
              blocked: user.emailRestrictions.keywordsBlocked,
              flagged: user.emailRestrictions.keywordsFlagged,
              suspended: user.emailRestrictions.keywordsSuspended,

              partialKeywordsBlocked:
                user.emailRestrictions.partialKeywordsBlocked,
              partialKeywordsFlagged:
                user.emailRestrictions.partialKeywordsFlagged,
              partialKeywordsSuspended:
                user.emailRestrictions.partialKeywordsSuspended,
            }}
          />
        );
      case BlockingTypes.DOMAIN:
        return (
          <BlockingTable
            type={BlockingTypes.DOMAIN}
            selected={selected}
            values={user.emailRestrictions.espsBlocked}
          />
        );
      case BlockingTypes.COUNTRIES:
        return (
          <BlockingTable
            type={BlockingTypes.COUNTRIES}
            selected={selected}
            disableWhitelistBlacklistToggle={
              user.emailRestrictions.countriesBlocked.length > 0 ||
              user.emailRestrictions.countriesWhitelisted.length > 0
            }
            values={
              user.options.countriesList === "BLACKLIST"
                ? (user.emailRestrictions.countriesBlocked || []).map(
                    (c) => countries.find((a) => a.key === c).text || ""
                  )
                : (user.emailRestrictions.countriesWhitelisted || []).map(
                    (c) => countries.find((a) => a.key === c).text || ""
                  )
            }
          />
        );
      case BlockingTypes.COUNTRY_CODE:
        return (
          <BlockingTable
            type={BlockingTypes.COUNTRY_CODE}
            selected={selected}
            disableWhitelistBlacklistToggle={
              user.emailRestrictions.countryCodesBlocked.length > 0 ||
              user.emailRestrictions.countryCodesWhitelisted.length > 0
            }
            values={
              user.options.countryCodesList === "BLACKLIST"
                ? user.emailRestrictions.countryCodesBlocked
                : user.emailRestrictions.countryCodesWhitelisted
            }
          />
        );
      case BlockingTypes.DEVICE_IP:
        return (
          <BlockingTable
            type={BlockingTypes.DEVICE_IP}
            selected={selected}
            values={user.emailRestrictions.deviceIpsBlocked}
          />
        );
      case BlockingTypes.DEVICE_ID:
        return (
          <BlockingTable
            type={BlockingTypes.DEVICE_ID}
            selected={selected}
            values={user.emailRestrictions.deviceIdsBlocked}
          />
        );
      default:
        <BlockingTable
          type={BlockingTypes.EMAIL}
          selected={selected}
          values={user.emailRestrictions.emailsBlocked}
        />;
    }
  }, [
    selected,
    user?.emailRestrictions,
    user?.options.countriesList,
    user?.options.countryCodesList,
  ]);

  return (
    <Container>
      <TextWrapper>
        <MainTitle>Blocking</MainTitle>
        <MainText>View and manage your blocking lists.</MainText>
      </TextWrapper>

      <div className="content">
        <BlockingHeader selected={selected} handleSelected={handleSelected} />
        {returnBody}
      </div>
    </Container>
  );
}
